import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  BookOpen,
  Calendar,
  Eye,
  Share2,
  Tag,
  ThumbsDown,
  ThumbsUp,
  User,
} from "@/icons";
import Button from "@/components/ui/Button.jsx";
import LayoutShell from "@/components/ui/LayoutShell.jsx";
import SettingsCard from "@/sot/primitives/SettingsCard.jsx";
import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import knowledgeBaseService from "@/services/knowledgeBaseService.js";

function normalizeKnowledgeError(message, t) {
  const raw = String(message || "").trim();
  const lower = raw.toLowerCase();

  if (!raw) return "";
  if (lower.includes("permission denied")) {
    return t(
      "knowledgeBase.errors.permission",
      "Knowledge base content is being prepared for this account. We’re keeping the experience available while access is completed."
    );
  }
  return raw;
}

function renderContentBlocks(content) {
  const chunks = String(content || "")
    .split(/\n\s*\n/)
    .map((chunk) => chunk.trim())
    .filter(Boolean);

  return chunks.map((chunk, index) => {
    const lines = chunk
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);

    const isBulletList = lines.every((line) => /^[-*]\s+/.test(line));
    const isNumberedList = lines.every((line) => /^\d+[.)]\s+/.test(line));

    if (isBulletList || isNumberedList) {
      const items = lines.map((line) =>
        line.replace(/^[-*]\s+/, "").replace(/^\d+[.)]\s+/, "")
      );
      const ListTag = isNumberedList ? "ol" : "ul";
      return (
        <ListTag
          key={`list-${index}`}
          className="mb-5 space-y-2 pl-5 text-body-large text-on-surface-var"
        >
          {items.map((item, itemIndex) => (
            <li key={`item-${index}-${itemIndex}`}>{item}</li>
          ))}
        </ListTag>
      );
    }

    return (
      <p key={`paragraph-${index}`} className="mb-5 text-body-large leading-7 text-on-surface-var">
        {chunk}
      </p>
    );
  });
}

export default function ArticleView() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, formatDate } = useLanguage();

  const [article, setArticle] = React.useState(null);
  const [relatedArticles, setRelatedArticles] = React.useState([]);
  const [userVote, setUserVote] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [voting, setVoting] = React.useState(false);
  const [error, setError] = React.useState("");
  const [shareNotice, setShareNotice] = React.useState("");

  const loadArticle = React.useCallback(async () => {
    if (!slug) return;

    try {
      setLoading(true);
      setError("");

      const { data, error: articleError } = await knowledgeBaseService.getArticle(slug);
      if (articleError || !data) {
        setError(
          normalizeKnowledgeError(
            articleError?.message ||
              t("knowledgeBase.article.notFound", "Article not found."),
            t
          )
        );
        setArticle(null);
        setRelatedArticles([]);
        return;
      }

      setArticle(data);

      if (data?.category) {
        const { data: related, error: relatedError } =
          await knowledgeBaseService.getRelatedArticles(data.id, data.category, 4);
        if (relatedError) {
          setRelatedArticles([]);
        } else {
          setRelatedArticles(related || []);
        }
      } else {
        setRelatedArticles([]);
      }
    } catch (nextError) {
      setError(
        normalizeKnowledgeError(
          nextError?.message ||
            t("knowledgeBase.errors.loadArticle", "Failed to load article."),
          t
        )
      );
    } finally {
      setLoading(false);
    }
  }, [slug, t]);

  React.useEffect(() => {
    loadArticle();
  }, [loadArticle]);

  React.useEffect(() => {
    if (!shareNotice) return undefined;
    const timeout = window.setTimeout(() => setShareNotice(""), 2200);
    return () => window.clearTimeout(timeout);
  }, [shareNotice]);

  const handleVote = async (isHelpful) => {
    if (!article?.id || voting) return;

    try {
      setVoting(true);
      setError("");

      const { error: voteError } = await knowledgeBaseService.voteArticle(
        article.id,
        isHelpful
      );
      if (voteError) {
        setError(
          normalizeKnowledgeError(
            voteError?.message ||
              t("knowledgeBase.article.voteFailed", "We couldn’t save your feedback."),
            t
          )
        );
        return;
      }

      setUserVote(isHelpful);
      setArticle((current) => {
        if (!current) return current;
        const helpfulVotes = Number(current.helpful_votes || 0);
        const nextHelpfulVotes = isHelpful
          ? helpfulVotes + (userVote === true ? 0 : 1)
          : Math.max(0, helpfulVotes - (userVote === true ? 1 : 0));

        return {
          ...current,
          helpful_votes: nextHelpfulVotes,
        };
      });
    } finally {
      setVoting(false);
    }
  };

  const handleShare = async () => {
    try {
      if (navigator?.share) {
        await navigator.share({
          title: article?.title,
          text: article?.excerpt,
          url: window.location.href,
        });
        return;
      }

      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(window.location.href);
        setShareNotice(t("knowledgeBase.article.linkCopied", "Article link copied."));
      }
    } catch {
      setShareNotice(t("knowledgeBase.article.linkCopyFailed", "Unable to share this link."));
    }
  };

  const formattedDate = article?.created_at
    ? formatDate(new Date(article.created_at), {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : t("knowledgeBase.common.recently", "Recently");

  if (loading) {
    return (
      <LayoutShell hideHeader>
        <div className="space-y-4">
          <TopBarCenter
            title={t("knowledgeBase.title", "Knowledge base")}
            onBack={() => navigate(-1)}
          />
          <SettingsCard className="px-6 py-12 text-center">
            <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-[rgb(var(--afrimed-primary))]" />
            <div className="mt-4 text-body-large text-on-surface-var">
              {t("knowledgeBase.article.loading", "Loading article...")}
            </div>
          </SettingsCard>
        </div>
      </LayoutShell>
    );
  }

  if (error || !article) {
    return (
      <LayoutShell hideHeader>
        <div className="space-y-4">
          <TopBarCenter
            title={t("knowledgeBase.title", "Knowledge base")}
            onBack={() => navigate(-1)}
          />
          <SettingsCard className="px-6 py-12 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-[20px] bg-[rgba(248,83,58,0.10)] text-[rgb(var(--schemes-critical))]">
              <BookOpen className="h-8 w-8" />
            </div>
            <div className="mt-5 text-headline-small text-on-surface">
              {t("knowledgeBase.article.unavailable", "Article unavailable")}
            </div>
            <div className="mx-auto mt-2 max-w-[520px] text-body-large text-on-surface-var">
              {error ||
                t(
                  "knowledgeBase.article.notFound",
                  "This article could not be found."
                )}
            </div>
            <div className="mt-6 flex flex-col justify-center gap-3 md:flex-row">
              <Button onClick={loadArticle}>
                {t("knowledgeBase.actions.tryAgain", "Try again")}
              </Button>
              <Link to="/knowledge-base">
                <Button
                  variant="ghost"
                  className="bg-[rgba(24,50,119,0.06)] text-on-surface hover:bg-[rgba(24,50,119,0.10)]"
                >
                  {t("knowledgeBase.actions.backToKnowledgeBase", "Back to knowledge base")}
                </Button>
              </Link>
            </div>
          </SettingsCard>
        </div>
      </LayoutShell>
    );
  }

  return (
    <LayoutShell hideHeader>
      <div className="space-y-4">
        <TopBarCenter
          title={t("knowledgeBase.title", "Knowledge base")}
          onBack={() => navigate(-1)}
        />

        <SettingsCard className="overflow-hidden">
          <div className="bg-[linear-gradient(135deg,rgba(29,78,216,0.12),rgba(0,189,196,0.06),rgba(255,255,255,0.98))] px-5 py-6 md:px-6 md:py-7">
            <div className="inline-flex items-center gap-2 rounded-full bg-[rgba(29,78,216,0.08)] px-3 py-1.5 text-label-large text-[rgb(var(--afrimed-primary))]">
              <BookOpen className="h-4 w-4" />
              <span>{article?.category || t("knowledgeBase.common.general", "General")}</span>
            </div>
            <div className="mt-4 text-headline-medium text-on-surface">
              {article?.title}
            </div>
            {article?.excerpt ? (
              <div className="mt-3 max-w-[760px] text-body-large text-on-surface-var">
                {article.excerpt}
              </div>
            ) : null}

            <div className="mt-5 flex flex-wrap items-center gap-4 text-body-small text-on-surface-var">
              <span className="flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5" />
                <span>{formattedDate}</span>
              </span>
              <span className="flex items-center gap-1">
                <Eye className="h-3.5 w-3.5" />
                <span>
                  {t("knowledgeBase.metrics.views", "{count} views", {
                    count: article?.view_count || 0,
                  })}
                </span>
              </span>
              <span className="flex items-center gap-1">
                <ThumbsUp className="h-3.5 w-3.5" />
                <span>
                  {t("knowledgeBase.metrics.helpful", "{count} helpful", {
                    count: article?.helpful_votes || 0,
                  })}
                </span>
              </span>
              <span className="flex items-center gap-1">
                <User className="h-3.5 w-3.5" />
                <span>
                  {article?.author?.display_name ||
                    t("knowledgeBase.article.authorFallback", "AfriMed™ Team")}
                </span>
              </span>
            </div>
          </div>
        </SettingsCard>

        {shareNotice ? (
          <SettingsCard className="px-4 py-3">
            <div className="text-body-medium text-[rgb(var(--afrimed-primary))]">
              {shareNotice}
            </div>
          </SettingsCard>
        ) : null}

        {error ? (
          <SettingsCard className="overflow-hidden">
            <div className="border-l-[4px] border-[rgb(var(--schemes-critical))] bg-[rgba(248,83,58,0.08)] px-4 py-4 text-body-medium text-[rgb(var(--schemes-critical))]">
              {error}
            </div>
          </SettingsCard>
        ) : null}

        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_300px]">
          <article className="space-y-4">
            <SettingsCard className="px-5 py-6 md:px-7">
              <div className="prose prose-neutral max-w-none">
                {renderContentBlocks(article?.content)}
              </div>

              {Array.isArray(article?.tags) && article.tags.length ? (
                <div className="mt-8 border-t border-[rgba(24,50,119,0.08)] pt-6">
                  <div className="flex items-center gap-2 text-title-medium text-on-surface">
                    <Tag className="h-4 w-4" />
                    <span>{t("knowledgeBase.article.tags", "Tags")}</span>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {article.tags.map((tag) => (
                      <span
                        key={tag}
                        className="rounded-full bg-[rgba(29,78,216,0.08)] px-3 py-1 text-label-large text-[rgb(var(--afrimed-primary))]"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}
            </SettingsCard>
          </article>

          <aside className="space-y-4">
            <SettingsCard className="px-5 py-5">
              <div className="text-title-large text-on-surface">
                {t("knowledgeBase.article.feedbackTitle", "Was this article helpful?")}
              </div>
              <div className="mt-3 text-body-medium text-on-surface-var">
                {t(
                  "knowledgeBase.article.feedbackBody",
                  "Your feedback helps us improve AfriMed™ guidance for everyone."
                )}
              </div>
              <div className="mt-4 flex gap-2">
                <Button
                  variant={userVote === true ? "default" : "tonal"}
                  size="sm"
                  className="flex-1"
                  onClick={() => handleVote(true)}
                  disabled={voting}
                >
                  <ThumbsUp className="mr-2 h-4 w-4" />
                  {t("knowledgeBase.article.helpfulYes", "Yes")}
                </Button>
                <Button
                  variant={userVote === false ? "outline" : "tonal"}
                  size="sm"
                  className="flex-1"
                  onClick={() => handleVote(false)}
                  disabled={voting}
                >
                  <ThumbsDown className="mr-2 h-4 w-4" />
                  {t("knowledgeBase.article.helpfulNo", "No")}
                </Button>
              </div>
            </SettingsCard>

            <SettingsCard className="px-5 py-5">
              <div className="text-title-large text-on-surface">
                {t("knowledgeBase.article.shareTitle", "Share this guide")}
              </div>
              <div className="mt-3 text-body-medium text-on-surface-var">
                {t(
                  "knowledgeBase.article.shareBody",
                  "Copy or share this article when a patient, doctor, or teammate needs the same answer."
                )}
              </div>
              <div className="mt-4">
                <Button variant="outline" fullWidth onClick={handleShare}>
                  <Share2 className="mr-2 h-4 w-4" />
                  {t("knowledgeBase.article.shareAction", "Share article")}
                </Button>
              </div>
            </SettingsCard>
          </aside>
        </div>

        {relatedArticles.length ? (
          <section className="space-y-4 pb-8">
            <div className="text-headline-small text-on-surface">
              {t("knowledgeBase.article.related", "Related articles")}
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              {relatedArticles.map((related) => (
                <Link key={related?.id} to={`/knowledge-base/article/${related?.slug}`}>
                  <SettingsCard className="h-full px-5 py-5 transition hover:-translate-y-[1px] hover:shadow-[0_22px_54px_rgba(15,23,42,0.10)] md:px-6">
                    <div className="text-title-large text-on-surface">{related?.title}</div>
                    <div className="mt-2 text-body-large text-on-surface-var">
                      {related?.excerpt ||
                        t(
                          "knowledgeBase.common.noExcerpt",
                          "Open this article to read the full guide."
                        )}
                    </div>
                    <div className="mt-4 flex flex-wrap items-center gap-4 text-body-small text-on-surface-var">
                      <span className="flex items-center gap-1">
                        <Eye className="h-3.5 w-3.5" />
                        <span>
                          {t("knowledgeBase.metrics.views", "{count} views", {
                            count: related?.view_count || 0,
                          })}
                        </span>
                      </span>
                      <span>
                        {related?.created_at
                          ? formatDate(new Date(related.created_at), {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })
                          : t("knowledgeBase.common.recently", "Recently")}
                      </span>
                    </div>
                  </SettingsCard>
                </Link>
              ))}
            </div>
          </section>
        ) : null}
      </div>
    </LayoutShell>
  );
}
